<!-- Единая карточка точки маршрута для заказа и рейса -->
<template>
  <div class="route-point-card">
    <div class="route-point-card__header">
      <div class="route-point-card__header-left">
        <slot name="drag-handle" />

        <BaseSequenceBadge :value="point.sequence_number" />

        <div class="route-point-card__title-block">
          <h4 class="route-point-card__title">
            {{ pointTypeTitle }}
          </h4>

          <div
            v-if="metaText"
            class="route-point-card__meta"
          >
            {{ metaText }}
          </div>
        </div>
      </div>

      <button
        type="button"
        class="route-point-card__edit"
        title="Редактировать"
        @click="$emit('edit', point.id)"
      >
        <AppIcon name="edit" :size="20" />
      </button>
    </div>

    <div class="route-point-card__content">
      <div class="route-point-card__body">
        <div class="route-point-card__info">
          <div class="route-point-card__item">
            <span class="route-point-card__label">
              <BaseInfoTooltip
                info-key="routePoint.common"
                :filled="Boolean(point.address)"
                variant="text"
              >
                Адрес
              </BaseInfoTooltip>
            </span>
            <span class="route-point-card__value">
              {{ point.address || "—" }}
            </span>
          </div>

          <div class="route-point-card__item">
            <span class="route-point-card__label">Вид погрузки</span>
            <span class="route-point-card__value">
              {{ loadingMethodTitle }}
            </span>
          </div>

          <div class="route-point-card__item">
            <span class="route-point-card__label">
              <BaseInfoTooltip
                info-key="routePoint.common"
                :filled="Boolean(point.planned_datetime || point.planned_at)"
                variant="text"
              >
                План. дата
              </BaseInfoTooltip>
            </span>
            <span class="route-point-card__value route-point-card__value--date">
              {{ plannedDateText }}
            </span>
          </div>

          <div class="route-point-card__item">
            <span class="route-point-card__label">
              <BaseInfoTooltip
                info-key="routePoint.common"
                :filled="Boolean(point.comment)"
                variant="text"
              >
                Комментарий
              </BaseInfoTooltip>
            </span>
            <span class="route-point-card__value">
              {{ point.comment || "—" }}
            </span>
          </div>
        </div>

        <div class="route-point-card__aside">
          <RoutePointMiniMap :lat="point.lat" :lon="point.lon" />
        </div>
      </div>

      <slot name="extra" />
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
import { useTripStore } from "@/stores/tripStore";

import AppIcon from "@/components/Icon/AppIcon.vue";
import RoutePointMiniMap from "@/components/RoutePoint/MiniMap.vue";

import { formatDateAndTime } from "@/utils/dateUtils";

export default {
  name: "RoutePointCard",

  components: {
    AppIcon,
    RoutePointMiniMap,
  },

  props: {
    point: {
      type: Object,
      required: true,
    },
    plannedDateText: {
      type: String,
      required: true,
    },
  },

  emits: ["edit"],

  computed: {
    tripStore() {
      return useTripStore();
    },

    pointTypeTitle() {
      return this.tripStore.formatPointType(this.point.point_type) || "Точка";
    },

    loadingMethodTitle() {
      return this.tripStore.formatLoadingMethod(this.point.loading_method) || "—";
    },

    metaText() {
      const items = [];

      if (this.point.created_at) {
        items.push(`Создано ${formatDateAndTime(this.point.created_at)}`);
      }

      if (this.point.updated_at) {
        items.push(`Обновлено ${formatDateAndTime(this.point.updated_at)}`);
      }

      return items.join(" · ");
    },
  },
};
</script>

<style scoped>
.route-point-card {
  overflow: hidden;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
  transition: box-shadow 0.2s;
}

.route-point-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.route-point-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 14px 9px;
  border-bottom: 1px solid #f1f3f5;
  background: #fcfcfd;
}

.route-point-card__header-left {
  display: flex;
  align-items: flex-start;
  min-width: 0;
  gap: 10px;
}

.route-point-card__header-left :slotted(.drag-handle) {
  display: flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: 0;
}

.route-point-card__title-block {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.route-point-card__title {
  min-width: 0;
  margin: 0;
  color: var(--color-dark);
  font-size: 15px;
  line-height: 1.25;
}

.route-point-card__edit {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: none;
  color: #999;
  cursor: pointer;
  transition: all 0.2s;
}

.route-point-card__edit:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--color-blue);
}

.route-point-card__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 14px 14px;
}

.route-point-card__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 208px;
  align-items: flex-start;
  gap: 12px;
}

.route-point-card__info {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  align-content: flex-start;
  min-width: 0;
  column-gap: 10px;
  row-gap: 7px;
}

.route-point-card__item {
  display: contents;
  font-size: 14px;
  line-height: 1.4;
}

.route-point-card__label {
  min-width: 0;
  color: #666;
  font-size: 13px;
}

.route-point-card__value {
  color: var(--color-dark);
  font-weight: 500;
  text-align: left;
  word-break: break-word;
}

.route-point-card__value--date {
  white-space: nowrap;
}

.route-point-card__aside {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.route-point-card__meta {
  color: #8a8a8a;
  font-size: 11px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 900px) {
  .route-point-card__body {
    grid-template-columns: 1fr;
  }

  .route-point-card__meta {
    white-space: normal;
  }
}

@media (max-width: 600px) {
  .route-point-card__header {
    align-items: flex-start;
  }

  .route-point-card__info {
    grid-template-columns: 1fr;
    row-gap: 2px;
  }

  .route-point-card__value:not(:last-child) {
    margin-bottom: 8px;
  }
}
</style>
